import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Cards,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Modal,
  FormField,
  Select,
  Input,
  Alert
} from '@cloudscape-design/components';

// Dummy data for recent projects
const recentProjects = [
  {
    id: '1',
    name: 'ECommerce Platform',
    status: 'In Progress',
    phase: 'Domain Analysis',
    lastAction: 'Domain Analysis completed',
    recommendedAction: 'Start Microservice Generation',
    type: 'info'
  },
  {
    id: '2',
    name: 'Banking Portal',
    status: 'Pending Review',
    phase: 'Technical Analysis',
    lastAction: 'Code analysis completed',
    recommendedAction: 'Review Analysis Results',
    type: 'warning'
  },
  {
    id: '3',
    name: 'CRM System',
    status: 'Active',
    phase: 'Microservice Generation',
    lastAction: 'Generated 2/5 microservices',
    recommendedAction: 'Continue Service Generation',
    type: 'success'
  }
];

const QuickActionsView = () => {
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  // New project modal states
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState({ label: 'Java Monolith', value: 'java' });

  const handleNewProject = () => {
    setShowAlert(true);
    setShowNewProjectModal(false);
    // Reset form
    setProjectName('');
    setProjectType({ label: 'Java Monolith', value: 'java' });
  };


  const navigate = useNavigate();

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Quick access to common actions and recent projects"
        >
          Quick Actions
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Primary Action Cards */}
        <Box>
          <SpaceBetween size="m" direction="horizontal">
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={() => navigate('/create-project')}
            >
              Start New Project
            </Button>
            <Button
              iconName="folder"
              onClick={() => navigate('/jobs')}
            >
              Resume Project
            </Button>
            <Button
              iconName="file"
            >
              View Documentation
            </Button>
          </SpaceBetween>
        </Box>

        {/* Alert for success message */}
        {showAlert && (
          <Alert
            type="success"
            dismissible
            onDismiss={() => setShowAlert(false)}
          >
            Project created successfully! You can now start the technical analysis phase.
          </Alert>
        )}

        {/* Recent Projects Cards */}
        <Cards
          cardDefinition={{
            header: item => (
              <Box variant="h4">
                {item.name}
              </Box>
            ),
            sections: [
              {
                content: item => (
                  <SpaceBetween size="s">
                    <StatusIndicator type={item.type}>
                      {item.status}
                    </StatusIndicator>
                    <Box variant="small">
                      <strong>Phase:</strong> {item.phase}
                    </Box>
                    <Box variant="small">
                      <strong>Last Action:</strong> {item.lastAction}
                    </Box>
                    <Box variant="small">
                      <strong>Recommended:</strong> {item.recommendedAction}
                    </Box>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        variant="link"
                        onClick={() => setSelectedProject(item)}
                      >
                        Resume
                      </Button>
                      <Button variant="link">
                        View Details
                      </Button>
                    </SpaceBetween>
                  </SpaceBetween>
                )
              }
            ]
          }}
          items={recentProjects}
          header={
            <Header
              variant="h3"
              counter={`(${recentProjects.length})`}
            >
              Recent Projects
            </Header>
          }
          trackBy="id"
        />

        {/* New Project Modal */}
        <Modal
          visible={showNewProjectModal}
          onDismiss={() => setShowNewProjectModal(false)}
          header="Start New Project"
          closeAriaLabel="Close modal"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowNewProjectModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleNewProject}>
                  Create Project
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="l">
            <FormField
              label="Project Name"
              description="Enter a unique name for your project"
            >
              <Input
                value={projectName}
                onChange={({ detail }) => setProjectName(detail.value)}
              />
            </FormField>
            <FormField
              label="Project Type"
              description="Select the type of application to modernize"
            >
              <Select
                selectedOption={projectType}
                onChange={({ detail }) => setProjectType(detail.selectedOption)}
                options={[
                  { label: 'Java Monolith', value: 'java' },
                  { label: '.NET Framework', value: 'dotnet' },
                  { label: 'Python Django', value: 'python' }
                ]}
              />
            </FormField>
          </SpaceBetween>
        </Modal>

        {/* Project Details Modal */}
        <Modal
          visible={!!selectedProject}
          onDismiss={() => setSelectedProject(null)}
          header={selectedProject?.name}
          closeAriaLabel="Close modal"
        >
          <SpaceBetween size="l">
            <Box variant="p">
              Current Phase: {selectedProject?.phase}
            </Box>
            <Box variant="p">
              Status: {selectedProject?.status}
            </Box>
            <Box variant="p">
              Last Action: {selectedProject?.lastAction}
            </Box>
            <Box variant="p">
              Recommended Next Step: {selectedProject?.recommendedAction}
            </Box>
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </Container>
  );
};

export default QuickActionsView;
